import {
  computed,
  effectScope,
  inject,
  mergeProps,
  nextTick,
  onScopeDispose,
  ref,
  unref,
  watch,
  watchEffect
} from 'vue';

import {
  bindProps,
  getCurrentInstance,
  IN_BROWSER,
  matchesSelector,
  propsFactory,
  refElement,
  unbindProps
} from '@components/utils';

import { useDelay } from '../../../composable';
import { CdsMenuSymbol } from '../../CdsMenu/shared';

import type { IDelayProps } from '../../../composable';
import type { ComponentPublicInstance, PropType, Ref, ComputedRef, ComponentInternalInstance, EffectScope } from 'vue';

export interface IActivatorProps extends IDelayProps {
  activator: 'parent' | string | Element | ComponentPublicInstance;
  activatorProps: Record<string, any>;
  target: 'parent' | 'cursor' | (string & {}) | Element | ComponentPublicInstance | [x: number, y: number] | undefined;
  openOnClick: boolean | undefined;
  openOnHover: boolean;
  openOnFocus: boolean | undefined;
  closeOnContentClick: boolean;
}

export interface IUseActivator {
  activatorEl: Ref<HTMLElement | undefined>;
  activatorRef: Ref<HTMLElement | undefined>;
  target: ComputedRef<HTMLElement | [x: number, y: number] | undefined>;
  targetEl: ComputedRef<HTMLElement | undefined>;
  targetRef: Ref<HTMLElement | undefined>;
  activatorEvents: ComputedRef<Partial<TAvailableEvents>>;
  contentEvents: ComputedRef<Partial<TAvailableEvents>>;
  scrimEvents: ComputedRef<Partial<TAvailableEvents>>;
}

type TAvailableEvents = {
  onClick: (event: MouseEvent) => void;
  onMouseenter: (event: MouseEvent) => void;
  onMouseleave: (event: MouseEvent) => void;
  onFocus: (event: FocusEvent) => void;
  onBlur: (event: FocusEvent) => void;
};

export const makeActivatorProps = propsFactory(
  {
    /**
     * Элемент, который будет активировать компонент
     */
    activator: {
      type: [String, Object] as PropType<'parent' | string | Element | ComponentPublicInstance>,
      default: null
    },
    /**
     * Свойства для элемента активатора
     */
    activatorProps: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    },
    /**
     * Для locationStrategy="connected" укажите элемент или массив координат x,y, относительно которого должен позиционироваться оверлей.
     * По умолчанию это будет элемент активатора.
     */
    target: {
      type: [String, Object] as PropType<
        'parent' | 'cursor' | (string & {}) | Element | ComponentPublicInstance | [x: number, y: number] | undefined
      >,
      default: undefined
    },
    /**
     * Показать компонент по клику на активатор
     */
    openOnClick: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined
    },
    /**
     * Показать компонент по наведению на активатор
     */
    openOnHover: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Показать компонент по фокусу на активатор
     */
    openOnFocus: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined
    },
    /**
     * Скрыть компонент по клику на контент
     */
    closeOnContentClick: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  'overlay-activator'
);

export function useActivator(
  props: IActivatorProps,
  { isActive, isTop }: { isActive: Ref<boolean>; isTop: Ref<boolean> }
): IUseActivator {
  const vm = getCurrentInstance('useActivator');
  const activatorEl = ref<HTMLElement>();

  let isHovered = false;
  let isFocused = false;
  let firstEnter = true;

  const openOnFocus = computed(() => props.openOnFocus || (props.openOnFocus == null && props.openOnHover));
  const openOnClick = computed(
    () => props.openOnClick || (props.openOnClick == null && !props.openOnHover && !unref(openOnFocus))
  );

  const { runOpenDelay, runCloseDelay } = useDelay(props, (value) => {
    if (
      value === ((props.openOnHover && isHovered) || (openOnFocus.value && isFocused)) &&
      !(props.openOnHover && isActive.value && !isTop.value)
    ) {
      if (isActive.value !== value) {
        firstEnter = true;
      }
      isActive.value = value;
    }
  });

  const cursorTarget = ref<[x: number, y: number]>();
  const availableEvents: TAvailableEvents = {
    onClick: (event: MouseEvent) => {
      event.stopPropagation();
      activatorEl.value = (event.currentTarget || event.target) as HTMLElement;

      if (!isActive.value) {
        cursorTarget.value = [event.clientX, event.clientY];
      }

      isActive.value = !isActive.value;
    },
    onMouseenter: (event: MouseEvent) => {
      if (event.sourceCapabilities?.firesTouchEvents) return;

      isHovered = true;
      activatorEl.value = (event.currentTarget || event.target) as HTMLElement;
      runOpenDelay();
    },
    onMouseleave: () => {
      isHovered = false;
      runCloseDelay();
    },
    onFocus: (event: FocusEvent) => {
      if (matchesSelector(event.target as HTMLElement, ':focus-visible') === false) return;

      isFocused = true;
      event.stopPropagation();
      activatorEl.value = (event.currentTarget || event.target) as HTMLElement;

      runOpenDelay();
    },
    onBlur: (event: FocusEvent) => {
      isFocused = false;
      event.stopPropagation();

      runCloseDelay();
    }
  };

  const activatorEvents = computed(() => {
    const events: Partial<typeof availableEvents> = {};

    if (openOnClick.value) {
      events.onClick = availableEvents.onClick;
    }

    if (props.openOnHover) {
      events.onMouseenter = availableEvents.onMouseenter;
      events.onMouseleave = availableEvents.onMouseleave;
    }

    if (openOnFocus.value) {
      events.onFocus = availableEvents.onFocus;
      events.onBlur = availableEvents.onBlur;
    }

    return events;
  });

  const contentEvents = computed(() => {
    const events: Record<string, EventListener> = {};

    if (props.openOnHover) {
      events.onMouseenter = () => {
        isHovered = true;
        runOpenDelay();
      };

      events.onMouseleave = () => {
        isHovered = false;
        runCloseDelay();
      };
    }

    if (props.openOnFocus) {
      events.onFocusin = () => {
        isFocused = true;
        runOpenDelay();
      };

      events.onFocusout = () => {
        isFocused = false;
        runCloseDelay();
      };
    }

    if (props.closeOnContentClick) {
      const menu = inject(CdsMenuSymbol, null);
      events.onClick = () => {
        isActive.value = false;
        menu?.closeParents();
      };
    }

    return events;
  });

  const scrimEvents = computed(() => {
    const events: Record<string, EventListener> = {};

    if (props.openOnHover) {
      events.onMouseenter = () => {
        if (firstEnter) {
          isHovered = true;
          firstEnter = false;
          runOpenDelay();
        }
      };
      events.onMouseleave = () => {
        isHovered = false;
        runCloseDelay();
      };
    }

    return events;
  });

  watch(isTop, (value) => {
    if (
      value &&
      ((props.openOnHover && !isHovered && (!openOnFocus.value || !isFocused)) ||
        (openOnFocus.value && !isFocused && (!props.openOnHover || !isHovered)))
    ) {
      isActive.value = false;
    }
  });

  watch(
    isActive,
    (value) => {
      if (!value) {
        setTimeout(() => {
          cursorTarget.value = undefined;
        });
      }
    },
    { flush: 'post' }
  );

  const activatorRef = ref<HTMLElement>();
  watchEffect(() => {
    if (!activatorRef.value) return;

    nextTick(() => {
      activatorEl.value = refElement(unref(activatorRef));
    });
  });

  const targetRef = ref<HTMLElement>();
  const target = computed(() => {
    if (props.target === 'cursor' && cursorTarget.value) return cursorTarget.value;
    if (unref(targetRef)) return refElement(unref(targetRef));
    return getTarget(props.target, vm) || unref(activatorEl);
  });
  const targetEl = computed(() => {
    return Array.isArray(unref(target)) ? undefined : (unref(target) as HTMLElement);
  });

  let scope: EffectScope;

  watch(
    () => !!props.activator,
    (value) => {
      if (value && IN_BROWSER) {
        scope = effectScope();
        scope.run(() => {
          _useActivator(props, vm, { activatorEl, activatorEvents });
        });
      } else if (scope) {
        scope.stop();
      }
    },
    { flush: 'post', immediate: true }
  );

  onScopeDispose(() => {
    scope?.stop();
  });

  return { activatorEl, activatorRef, target, targetEl, targetRef, activatorEvents, contentEvents, scrimEvents };
}

function _useActivator(
  props: IActivatorProps,
  vm: ComponentInternalInstance,
  { activatorEl, activatorEvents }: Pick<ReturnType<typeof useActivator>, 'activatorEl' | 'activatorEvents'>
) {
  watch(
    () => props.activator,
    (value, oldValue) => {
      if (oldValue && value !== oldValue) {
        const activator = getActivator(oldValue);
        activator && unbindActivatorProps(activator);
      }

      if (value) {
        nextTick(() => bindActivatorProps());
      }
    },
    { immediate: true }
  );

  watch(
    () => props.activatorProps,
    () => {
      bindActivatorProps();
    }
  );

  onScopeDispose(() => {
    unbindActivatorProps();
  });

  function bindActivatorProps(el = getActivator(), _props = props.activatorProps) {
    if (!el) return;

    bindProps(el, mergeProps(unref(activatorEvents), _props));
  }

  function unbindActivatorProps(el = getActivator(), _props = props.activatorProps) {
    if (!el) return;

    unbindProps(el, mergeProps(unref(activatorEvents), _props));
  }

  function getActivator(selector = props.activator): HTMLElement | undefined {
    const activator = getTarget(selector, vm);

    // The activator should only be a valid element (Ignore comments and text nodes)
    activatorEl.value = activator?.nodeType === Node.ELEMENT_NODE ? activator : undefined;

    return activatorEl.value as HTMLElement;
  }
}

function getTarget<
  T extends 'parent' | string | Element | ComponentPublicInstance | [x: number, y: number] | undefined
>(
  selector: T,
  vm: ComponentInternalInstance
): HTMLElement | undefined | (T extends any[] ? [x: number, y: number] : never) {
  if (!selector) return;

  let target;
  if (selector === 'parent') {
    let el = vm?.proxy?.$el?.parentNode;
    while (el?.hasAttribute('data-no-activator')) {
      el = el.parentNode;
    }
    target = el;
  } else if (typeof selector === 'string') {
    // Selector
    target = document.querySelector(selector);
    // @ts-ignore
  } else if ('$el' in selector) {
    // Component (ref)
    target = selector.$el;
  } else {
    // HTMLElement | Element | [x, y]
    target = selector;
  }

  return target;
}
